import { useCallback, RefObject } from "react";
import usePDA1091_R01Store from "./PDA1091_R01Store";
import { scanItemR02, adjustStockR02 } from "src/apis/PDA1091_R01Api";
import { useToast } from "src/hooks/useToast";
import type {
  PDA1091_R01AdjustRequest,
  AuditItem,
} from "./PDA1091_R01Dto";

export const usePDA1091_R01Content = (scanInputRef?: RefObject<HTMLInputElement>) => {
  const {
    warehouseCd,
    scanValue,
    setScanValue,
    setScannedItem,
    actualStock,
    setLoading,
    auditItems,
    addAuditItem,
    setAuditItems,
    removeAuditItem,
    clearAuditItems,
    resetForm,
  } = usePDA1091_R01Store();

  const { showToast } = useToast();

  /** 품목 스캔 → 지정 창고 시스템 총재고 조회 */
  const handleScanItem = useCallback(async (): Promise<boolean> => {
    const trimmed = scanValue.trim().toUpperCase();
    if (!trimmed) {
      showToast("error", "품목을 스캔하세요.");
      return false;
    }
    if (!warehouseCd) {
      showToast("error", "실사 창고를 먼저 선택하세요.");
      return false;
    }
    try {
      setLoading(true);
      const response = await scanItemR02(warehouseCd, trimmed);
      const data = response?.data?.data;
      if (!data) {
        showToast("error", "스캔 결과를 받지 못했습니다.");
        setScanValue("");
        return false;
      }
      setScannedItem(data);
      usePDA1091_R01Store.getState().setActualStock(null);
      showToast("success", `${data.itemNm} (현재고 ${data.currentStock.toLocaleString()})`);
      return true;
    } catch (error: unknown) {
      console.error("품목 스캔 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "품목 스캔에 실패했습니다.";
      showToast("error", message);
      setScanValue("");
      setScannedItem(null);
      usePDA1091_R01Store.getState().setActualStock(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [scanValue, warehouseCd, setLoading, setScanValue, setScannedItem, showToast]);

  /** 실재고 입력 후 실사 대기 목록에 추가 */
  const handleAddToGrid = useCallback(() => {
    const { scannedItem } = usePDA1091_R01Store.getState();
    if (!scannedItem) {
      showToast("error", "품목을 먼저 스캔하세요.");
      return;
    }
    if (actualStock === null || actualStock === undefined) {
      showToast("error", "실재고를 입력하세요.");
      return;
    }
    if (actualStock < 0) {
      showToast("error", "실재고는 0보다 작을 수 없습니다.");
      return;
    }
    if (actualStock === scannedItem.currentStock) {
      showToast("warning", "현재고와 실재고가 동일합니다.");
      return;
    }

    const diff = actualStock - scannedItem.currentStock;

    const exists = auditItems.find((i) => i.itemCd === scannedItem.itemCd);
    if (exists) {
      setAuditItems(
        auditItems.map((i) =>
          i.itemCd === scannedItem.itemCd
            ? { ...i, actualStock, diffQty: diff }
            : i
        )
      );
      showToast("success", `${scannedItem.itemNm} 실재고 갱신`);
    } else {
      const item: AuditItem = {
        itemCd: scannedItem.itemCd,
        itemNm: scannedItem.itemNm,
        itemSpec: scannedItem.itemSpec,
        itemUnit: scannedItem.itemUnit,
        itemStatus: scannedItem.itemStatus,
        currentStock: scannedItem.currentStock,
        actualStock,
        diffQty: diff,
      };
      addAuditItem(item);
      showToast("success", `실사 대기 추가 (${auditItems.length + 1}건)`);
    }

    resetForm();
    setTimeout(() => scanInputRef?.current?.focus(), 50);
  }, [actualStock, auditItems, addAuditItem, setAuditItems, resetForm, showToast, scanInputRef]);

  const handleRemoveFromGrid = useCallback(
    (itemCd: string) => {
      removeAuditItem(itemCd);
      showToast("info", "실사 대기 목록에서 삭제되었습니다.");
    },
    [removeAuditItem, showToast]
  );

  /** 실사 등록 (백엔드 일괄, 자재별 성공/실패) */
  const handleSaveAll = useCallback(async () => {
    if (auditItems.length === 0) {
      showToast("error", "실사할 항목이 없습니다.");
      return;
    }
    try {
      setLoading(true);
      const request: PDA1091_R01AdjustRequest = {
        warehouseCd,
        logicCenter: "",
        itemStatus: "",
        adjustReason: "재고실사",
        remark: "",
        itemList: auditItems.map((i) => ({
          itemCd: i.itemCd,
          currentStock: i.currentStock,
          actualStock: i.actualStock,
        })),
      };
      const response = await adjustStockR02(request);
      const result = response?.data?.data;

      if (result) {
        if (result.failCount === 0) {
          showToast("success", `재고실사 완료 (${result.successCount}건)`);
          clearAuditItems();
        } else {
          const failed = result.results
            .filter((r) => !r.success)
            .map((r) => r.itemCd)
            .join(", ");
          showToast(
            "warning",
            `${result.successCount}건 완료 / ${result.failCount}건 실패 (${failed})`
          );
          const failedCds = new Set(result.results.filter((r) => !r.success).map((r) => r.itemCd));
          setAuditItems(auditItems.filter((i) => failedCds.has(i.itemCd)));
        }
      }
    } catch (error: unknown) {
      console.error("재고실사 실패:", error);
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "재고실사에 실패했습니다.";
      showToast("error", message);
    } finally {
      setLoading(false);
    }
  }, [auditItems, warehouseCd, setLoading, clearAuditItems, setAuditItems, showToast]);

  const handleCancel = useCallback(() => {
    if (auditItems.length > 0) {
      if (!window.confirm(`실사 대기중인 ${auditItems.length}건이 있습니다.\n모두 취소하시겠습니까?`)) {
        return;
      }
      clearAuditItems();
      showToast("info", "실사 대기 목록이 초기화되었습니다.");
    }
    resetForm();
  }, [auditItems, clearAuditItems, resetForm, showToast]);

  return {
    handleScanItem,
    handleAddToGrid,
    handleRemoveFromGrid,
    handleSaveAll,
    handleCancel,
  };
};
